
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { UserWithPromoCode, ROLES, Role, updateUserByAdmin } from "utils";

interface RoleDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  initialData: UserWithPromoCode | null;
}

export const RoleDialog: React.FC<RoleDialogProps> = ({
  open,
  onClose,
  onSave,
  initialData,
}) => {
  const [role, setRole] = useState<Role | "">("");
  const [countries, setCountries] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initialData) {
      setRole(initialData.role);
      // Join the country array into a comma-separated string for the text field
      setCountries(initialData.country?.join(", ") || "");
    } else {
      setRole("");
      setCountries("");
    }
    setError(null);
  }, [initialData, open]);

  const handleRoleChange = (event: SelectChangeEvent) => {
    setRole(event.target.value as Role);
  };

  const handleSave = async () => {
    if (!initialData) return;

    // Convert comma-separated string back to an array, trimming whitespace and removing empty strings
    const countryArray = countries.split(',').map(c => c.trim().toUpperCase()).filter(Boolean);

    const payload = {
      role: role || initialData.role,
      country: countryArray,
    };

    try {
      await updateUserByAdmin(initialData.userID, payload);
      onSave();
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to update user.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Role and Permissions</DialogTitle>
      <DialogContent>
        <Box component="form" sx={{ mt: 2 }}>
          <FormControl fullWidth margin="normal">
            <InputLabel id="role-select-label">Role</InputLabel>
            <Select
              labelId="role-select-label"
              value={role}
              label="Role"
              onChange={handleRoleChange}
            >
              {Object.values(ROLES).map((r) => (
                <MenuItem key={r} value={r}>
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="normal"
            fullWidth
            label="Assigned Countries (comma-separated)"
            value={countries}
            onChange={(e) => setCountries(e.target.value)}
            helperText="e.g., US, PL, CD, FR"
          />
          {error && <Typography color="error">{error}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
